// WINESS CUSTOM: Product image "zoom circle" magnifier.
// Click a product image → a circular magnifier lens appears at the cursor and
// follows the pointer, zooming the area around it (2.5×). No modal / no overlay.
//
// Dismissed by: clicking the image again, moving the pointer off the image,
// scrolling, tapping/clicking anywhere else, Escape, resize, or tab blur.
// The scroll/outside-tap paths matter on touch, where `pointerleave` fires on
// `pointerup` — i.e. *before* the click that opens the lens — so it can never
// close the lens it preceded.
//
// Replaces the default full-page lightbox (image_zoom == 'lightbox'). A
// capture-phase click listener intercepts the theme's <modal-opener> so the
// lightbox never opens. No edits to global.js / product-modal.js.

(function () {
  if (window.WinessZoomCircle) return;
  window.WinessZoomCircle = true;

  var ZOOM = 2.5; // magnification factor

  var activeOpener = null;
  var loaded = Object.create(null); // url → true once downloaded

  // Pick the highest-resolution URL from an <img> srcset for a crisp zoom.
  function largestSrc(img) {
    var srcset = img.getAttribute('srcset') || '';
    var best = img.currentSrc || img.getAttribute('src') || '';
    var bestPx = 0;
    if (srcset) {
      srcset.split(',').forEach(function (part) {
        var bits = part.trim().split(/\s+/);
        if (!bits[0]) return;
        var m = (bits[1] || '').match(/^(\d+(?:\.\d+)?)(w|x)$/);
        if (!m) return;
        var num = parseFloat(m[1]);
        var px = m[2] === 'w' ? num : num * (img.naturalWidth || 1);
        if (px > bestPx) {
          bestPx = px;
          best = bits[0];
        }
      });
    }
    return best;
  }

  // Warm the browser cache for the high-res URL so the lens can paint it
  // immediately. Safe to call repeatedly.
  function preload(url, onReady) {
    if (!url) return;
    if (loaded[url]) {
      if (onReady) onReady();
      return;
    }
    var pre = new Image();
    pre.onload = function () {
      loaded[url] = true;
      if (onReady) onReady();
    };
    pre.src = url;
  }

  // Paint the lens straight away with the variant the page already downloaded,
  // then upgrade to the high-res one once it arrives. Zoom level is derived from
  // the on-screen size, not the source resolution, so the swap is seamless — no
  // white flash while the big file is in flight.
  function setLensImage(lens, img) {
    var immediate = img.currentSrc || img.getAttribute('src') || '';
    var best = largestSrc(img);

    if (immediate) lens.style.backgroundImage = "url('" + immediate + "')";
    if (!best || best === immediate) return;

    preload(best, function () {
      // The lens may have been dismissed while the image was downloading.
      if (lens.isConnected) lens.style.backgroundImage = "url('" + best + "')";
    });
  }

  // Position the lens centred on (x, y) and show the zoomed region under it.
  function position(lens, img, x, y) {
    var rect = img.getBoundingClientRect();
    var w = lens.offsetWidth || 180;
    var h = lens.offsetHeight || 180;
    lens.style.left = x - w / 2 + 'px';
    lens.style.top = y - h / 2 + 'px';
    lens.style.backgroundSize = rect.width * ZOOM + 'px ' + rect.height * ZOOM + 'px';
    // The image point under the cursor maps to the centre of the lens.
    lens.style.backgroundPosition =
      (w / 2 - (x - rect.left) * ZOOM) + 'px ' + (h / 2 - (y - rect.top) * ZOOM) + 'px';
  }

  function activate(opener, img) {
    if (opener.__winessLens) {
      deactivate(opener); // already active → toggle off
      return;
    }
    closeActive(); // only ever one lens on screen

    var lens = document.createElement('div');
    lens.className = 'winess-zoom-circle';
    setLensImage(lens, img);
    document.body.appendChild(lens);
    opener.__winessLens = lens;
    activeOpener = opener;

    var move = function (e) {
      if (!img.isConnected) {
        closeActive();
        return;
      }
      position(lens, img, e.clientX, e.clientY);
    };
    var leave = function () {
      deactivate(opener);
    };
    opener.addEventListener('pointermove', move);
    opener.addEventListener('pointerleave', leave);
    opener.__winessCleanup = function () {
      opener.removeEventListener('pointermove', move);
      opener.removeEventListener('pointerleave', leave);
    };
  }

  function deactivate(opener) {
    if (!opener) return;
    if (opener.__winessLens) {
      opener.__winessLens.remove();
      opener.__winessLens = null;
    }
    if (opener.__winessCleanup) {
      opener.__winessCleanup();
      opener.__winessCleanup = null;
    }
    if (activeOpener === opener) activeOpener = null;
  }

  function closeActive() {
    if (activeOpener) deactivate(activeOpener);
  }

  // Intercept clicks on product image openers in the CAPTURE phase so the theme's
  // <modal-opener> (full-page lightbox) never receives the event.
  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      if (!target || !target.closest) return;
      var opener = target.closest('.product__modal-opener--image');
      if (!opener) return;
      var img = opener.querySelector('.product__media img');
      if (!img) return;
      event.preventDefault();
      event.stopPropagation();
      activate(opener, img);
      if (opener.__winessLens) {
        position(opener.__winessLens, img, event.clientX, event.clientY);
      }
    },
    true
  );

  // Prefetch the high-res image on hover so desktop clicks open at full quality
  // with no visible upgrade. Pointer-hover only, to avoid spending mobile data.
  if (window.matchMedia && window.matchMedia('(hover: hover)').matches) {
    document.addEventListener(
      'pointerenter',
      function (event) {
        var target = event.target;
        if (!target || !target.closest) return;
        var opener = target.closest('.product__modal-opener--image');
        if (!opener || opener.__winessPrefetched) return;
        var img = opener.querySelector('.product__media img');
        if (!img) return;
        opener.__winessPrefetched = true;
        preload(largestSrc(img));
      },
      true
    );
  }

  // --- Dismissal paths ------------------------------------------------------
  // Capture phase throughout, so a lens never survives into a modal, a drawer,
  // or a scrolled-away viewport. The lens is position:fixed, so leaving it open
  // across a scroll would leave it floating over unrelated content.

  // `capture: true` also catches scrolls inside nested scrollers (media slider,
  // enquiry drawer), which don't bubble to window.
  window.addEventListener('scroll', closeActive, { passive: true, capture: true });
  window.addEventListener('resize', closeActive);
  window.addEventListener('orientationchange', closeActive);
  window.addEventListener('pagehide', closeActive);

  // Any press outside the active opener closes the lens — including the press
  // that opens an enquiry form or drawer, since this runs before their handlers.
  document.addEventListener(
    'pointerdown',
    function (event) {
      if (!activeOpener) return;
      var target = event.target;
      if (target && target.nodeType === 1 && activeOpener.contains(target)) return;
      closeActive();
    },
    true
  );

  document.addEventListener('keydown', function (event) {
    if (event.key === 'Escape' || event.key === 'Esc') closeActive();
  });

  document.addEventListener('visibilitychange', function () {
    if (document.hidden) closeActive();
  });
})();
